import { Alert, Button, Container, Header, SpaceBetween, StatusIndicator } from "@cloudscape-design/components";
import { Layout } from "../../components/layout";
import { useAuth } from "react-oidc-context";
import { useEffect, useState, useCallback } from "react";
import AdminDemosTable from "../../components/admin/DemosTable";
import { getEndpoint } from "../../utils";
import AdminAssetsTable from "../../components/admin/AssetsTable";
import { RecordPageView } from "../../utils/rum";

export default function Dashboard() {
    RecordPageView();
    const auth = useAuth();
    const [loading, setLoading] = useState(true);
    const [demos, setDemos] = useState([]);
    const [isPipelineRunning, setIsPipelineRunning] = useState(false);
    const [lastExecutionTime, setLastExecutionTime] = useState('');
    const fetchPipelineStatus = useCallback(async (token: string) => {
        try {
            const pipelineStatusEndpoint = getEndpoint('/api/admin/pipeline');
            const response = await fetch(pipelineStatusEndpoint, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (response.ok) {
                const data = await response.json();
                setIsPipelineRunning(data.isRunning);
                setLastExecutionTime(data.lastExecutionTime);
            } else {
                const errorText = await response.text();
                console.log(errorText);
            }
        } catch (e) {
            console.error(e);
        }
    }, []);

    const fetchDemos = useCallback(async (token: string) => {
        try {
            const endpoint = getEndpoint('/api/admin/demos?pendingDeployment=true');
            const response = await fetch(endpoint, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                const errorText = await response.text();
                console.error(errorText);
                return;
            }
            const data = await response.json();
            setDemos(data);
            setLoading(false);
            
            if (data.length > 0 && data.some((demo: any) => demo.deployedToPortal === false)) {
                await fetchPipelineStatus(token);
            }
        } catch (e) {
            console.error(e);
            setLoading(false);
        }
    }, [fetchPipelineStatus]);

    useEffect(() => {
        if (!auth.isAuthenticated) {
            auth.signinRedirect({
                url_state: encodeURIComponent(window.location.pathname)
            });
            return;
        }

        if (auth.user?.profile?.['custom:groups'] !== undefined && auth.user?.profile?.['custom:groups'] !== null) {
            const groups = JSON.parse(auth.user.profile['custom:groups'] as string);
            if (!groups.includes('admin')) {
                window.location.href = '/';
                return;
            }
        }

        auth.startSilentRenew();
        setLoading(false);

        const token = auth.user!.id_token;
        if (!token) return;

        fetchDemos(token);
    }, [auth, fetchDemos]);

    const handleReleaseUpdates = useCallback(async () => {
        try {
            const token = auth.user!.id_token;
            const endpoint = getEndpoint('/api/admin/pipeline');
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
            });
            
            if (response.ok) {
                setIsPipelineRunning(true);
                const currentTime = new Date().toISOString();
                setLastExecutionTime(currentTime);
            } else {
                const errorText = await response.text();
                console.log(errorText);
            }
        } catch (e) {
            console.error(e);
        }
    }, [auth]);

    if (loading) {
        return (
            <SpaceBetween direction='vertical' size='m' alignItems='center'>
                <StatusIndicator type="loading">Loading...</StatusIndicator>
            </SpaceBetween>)
    }
    return (
        <Layout>
            <SpaceBetween direction='vertical' size='l'>
                <Header variant='awsui-h1-sticky' description="Welcome to the Admin Dashboard.">Admin Dashboard</Header>
                {demos.filter((demo: any) => demo.deployedToPortal === false).length > 0 ?
                    (<Alert type="warning" header="Pending Deployments" dismissible={false} action={
                        <Button variant="normal" disabled={isPipelineRunning} disabledReason="An update is currently running. Please wait for it to complete before trying to update." onClick={handleReleaseUpdates}>Release Updates</Button>
                    }>
                        <SpaceBetween direction='vertical' size='m' alignItems='center'>
                            <span>There are {demos.filter((demo: any) => demo.deployedToPortal === false).length} demos pending deployment.</span>
                            <span>Last Update: {lastExecutionTime}</span>
                        </SpaceBetween></Alert>) : null}
                <AdminDemosTable key="Admin-Demos-Table" />
                <AdminAssetsTable key="Admin-Assets-Table" />
                <Container>
                    <Header
                        variant="h2"
                        actions={
                            <Button onClick={() => window.location.href = '/admin/faq'}>
                                Manage FAQs
                            </Button>
                        }
                    >
                        FAQs
                    </Header>
                </Container>
            </SpaceBetween>
        </Layout>
    )
}
